export const StateCode = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  LOADING: 'LOADING',
} as const;

/**
 * Represents an error that occurred during a result operation.
 *
 * @template E - Enum of possible error codes.
 *
 * @property {E} code - The error code.
 * @property {any} exception - The original exception thrown.
 */
export interface ResultError<E = never> {
  code: E;
  exception: any;
}

/**
 * Represents an empty or uninitialized execution. This should be the initial state of a result.
 *
 * @property {never} [state] - Does not have a state.
 * @property {never} [errors] - Cannot have errors.
 */
export interface EmptyState {
  state?: never;
  errors?: never;
}

/**
 * Represents an execution in transit. Exepect SuccessfulResult or FailedResult to follow.
 * Data and errors may be preserved from previous execution.
 *
 * @template E - Enum of possible error codes.
 *
 * @property {StateCode.LOADING} state - Currently executing.
 * @property {ResultError} [errors] - Errors from previous execution. May be undefined.
 */
export interface LoadingState<E = never> {
  state: typeof StateCode.LOADING;
  errors?: ResultError<E>[];
}

/**
 * Represents a successful execution.
 * Previous errors may be present.
 *
 * @template E - Expected error codes.
 *
 * @property {StateCode.SUCCESS} state - Execution successful.
 * @property {ResultError} [errors] - Errors from previous execution. May be undefined.
 */
export interface SuccessfulResult<E = never, T = unknown> {
  state: typeof StateCode.SUCCESS;
  errors?: ResultError<E>[];
  data?: T;
}

/**
 * Represents a failed execution.
 * Previous data may be present.
 *
 * @template E - Enum of possible error codes.
 *
 * @property {StateCode.FAILED} state - Execution failed.
 * @property {ResultError} errors - Errors occurred during the execution.
 */
export interface FailedResult<E = never> {
  state: typeof StateCode.FAILED;
  errors?: ResultError<E>[];
}

/**
 * Represents the result of an asynchronous call.
 * May be in one of two results:
 * - SuccessfulResult: Data returned successfully.
 * - FailedResult: Some error occurred during execution.
 *
 * @template E - Expected error codes.
 * @template T - Expected data type returned on success.
 *
 * @property {boolean} [loading] - Indicates if an execution is in progress.
 * @property {ResultError} [errors] - Errors that occurred during the execution.
 */
export type AsyncRequestResult<E, T = unknown> =
  | SuccessfulResult<E, T>
  | FailedResult<E>;

/**
 * Represents state kept on a provider about an async function call.
 * May be in one of four states:
 * - EmptyState: Uninitialized state.
 * - LoadingState: Execution in progress.
 * - SuccessfulResult: Data returned successfully.
 * - FailedResult: Some error occurred during execution.
 *
 * @template E - Expected error codes.
 *
 * @property {boolean} [loading] - Indicates if an execution is in progress.
 * @property {ResultError} [errors] - Errors that occurred during an execution.
 */
export type AsyncHookState<E, T = unknown> =
  | EmptyState
  | LoadingState<E>
  | AsyncRequestResult<E, T>;
